import { action, computed, IObservableValue, observable, set } from 'mobx';
import { addToMap, setMap, toArray } from '../Utils/Array';
import { bind } from '../Utils/Bind';
import { HttpClient, IAccessTokenStore, IConfig } from '../Utils/HttpClient';
import { ConnectionManager } from './ConnectionManager';
import { uuidv4generate } from './UUID';

interface IToastTask {
    toast?: IObservableValue<boolean>;
    toastHandle?: number;
}

export class TasksStore {
    @computed
    public get toastTasks() {
        const tasks = toArray(this.tasks).filter(t => t.toast?.get());

        return tasks;
    }
    public static readonly toastTimeout: number = 10000;
    public constructor(httpClient: HttpClient, accessTokenStore: IAccessTokenStore) {
        this.httpClient = httpClient;

        this.connectionManager = new ConnectionManager('/sockets/tasks', accessTokenStore);
        this.connectionManager.connection.on('OnUserTaskChanged', this.onTaskChanged);
    }

    @action
    public async loadUserTasksAsync(config?: IConfig) {
        const tasks = await this.httpClient.get<Mri.UserTasks.UserTask[]>(`/api/v1/tasks`, config);
        if (tasks) addToMap(this.tasks, tasks, t => (t as Mri.UserTasks.UserTask).userTaskGuid);
    }

    @action
    public startSessionTask(name: string): SessionTask {
        const task: SessionTask & IToastTask = new SessionTask(name);
        task.state = Mri.UserTasks.UserTaskState.Running;
        this.tasks.set(task.sessionTaskGuid, task);

        this.toastTask(task);

        return task;
    }

    @action
    public toastNotification(name: string, onClick?: () => void): SessionTask {
        const task: SessionTask & IToastTask = new SessionTask(name);
        task.state = Mri.UserTasks.UserTaskState.Completed;
        (task as any).isNotification = true;
        (task as any).onClick = onClick;
        this.tasks.set(task.sessionTaskGuid, task);

        this.toastTask(task);

        return task;
    }

    @action
    public addNotification(name: string, onClick?: () => void): SessionTask {
        const task: SessionTask & IToastTask = new SessionTask(name);
        task.state = Mri.UserTasks.UserTaskState.Completed;
        (task as any).isNotification = true;
        (task as any).onClick = onClick;
        this.tasks.set(task.sessionTaskGuid, task);

        return task;
    }

    @action
    public toastErrorTask(name: string, exception: any): SessionTask {
        const task: SessionTask & IToastTask = new SessionTask(name);
        task.error(exception);
        this.tasks.set(task.sessionTaskGuid, task);

        this.toastTask(task);

        return task;
    }

    @action
    public async dismissTaskAsync(task: SessionTask | Mri.UserTasks.UserTask) {
        if (isUserTask(task)) await this.dismissUserTaskAsync(task);
        else this.dismissSessionsTask(task);
    }

    @bind
    @action
    public dismissToast(taskOrGuid: string | IToastTask) {
        const task = typeof taskOrGuid == 'string' ? this.tasks.get(taskOrGuid) : taskOrGuid;

        if (!task) throw new Error('Task does not exist');

        if (task.toast) task.toast.set(false);
        window.clearTimeout(task.toastHandle);
        task.toastHandle = undefined;
    }

    @bind
    @action
    public dismissAllToasts() {
        for (const toastTask of this.toastTasks) {
            this.dismissToast(toastTask);
        }
    }

    @bind
    @action
    public async clearAll() {
        try {
            const promises = toArray(this.tasks).map(t => this.dismissTaskAsync(t));
            await Promise.all(promises);
        } catch (e: any) {
            console.error('Failed to Dismiss', e);
        }
    }

    @bind
    @action
    private onTaskChanged(task: Mri.UserTasks.UserTask) {
        const existingTask = this.tasks.get(task.userTaskGuid);

        if (existingTask == null) {
            const observableTask = observable(task);
            this.tasks.set(task.userTaskGuid, observableTask);
            this.toastTask(observableTask as IToastTask);
        } else {
            set(existingTask!, task);
        }
    }

    @action
    private async dismissUserTaskAsync(task: Mri.UserTasks.UserTask) {
        await this.httpClient.delete(`/api/v1/tasks/${task.userTaskGuid}`);

        this.tasks.delete(task.userTaskGuid);
    }

    @bind
    @action
    private dismissSessionsTask(task: SessionTask) {
        const onDismiss = (task as any).onDismiss;
        if (onDismiss) onDismiss();
        this.tasks.delete(task.sessionTaskGuid);
    }

    @bind
    @action
    private toastTask(task: IToastTask) {
        task.toast = observable.box(true);
        task.toastHandle = window.setTimeout(() => this.dismissToast(task), TasksStore.toastTimeout);
    }

    @observable
    public tasks = new Map<string, (Mri.UserTasks.UserTask & IToastTask) | (SessionTask & IToastTask)>();
    public readonly connectionManager: ConnectionManager;
    private readonly httpClient: HttpClient;
}

export class SessionTask {
    public constructor(name: string) {
        this.sessionTaskGuid = uuidv4generate();
        this.name = name;
        this.state = Mri.UserTasks.UserTaskState.Idle;
    }

    @action
    public error(exception: any) {
        this.state = Mri.UserTasks.UserTaskState.Failed;
        this.exception = exception;
    }

    @action
    public complete() {
        this.state = Mri.UserTasks.UserTaskState.Completed;
    }

    @observable
    public sessionTaskGuid: string;
    @observable
    public name: string;
    @observable
    public progress?: number;
    @observable
    public state: Mri.UserTasks.UserTaskState;
    @observable
    public exception: any;
}

export function isUserTask(value: Mri.UserTasks.UserTask | SessionTask): value is Mri.UserTasks.UserTask {
    return (value as Mri.UserTasks.UserTask).userTaskGuid != undefined;
}
